// apps/web/src/components/analisis/ResumenMetricas.tsx - ACTUALIZADO PARA LÍNEAS
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnalisisResponse } from '../../services/analisisService';

// ===============================
// INTERFACES
// ===============================

interface ResumenMetricasProps {
  analisis: AnalisisResponse;
}

interface MetricaCardProps {
  valor: string | number;
  label: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
  icon?: string;
}

// ===============================
// COMPONENTES
// ===============================

const MetricaCard: React.FC<MetricaCardProps> = ({ valor, label, color, icon }) => {
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600'
  };

  return (
    <div className="text-center">
      <div className={`text-2xl font-bold ${colorClasses[color]} flex items-center justify-center gap-1`}>
        {icon && <span className="text-lg">{icon}</span>}
        {valor}
      </div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
};

export const ResumenMetricas: React.FC<ResumenMetricasProps> = ({ analisis }) => {
  const { t } = useTranslation();
  const { total_reportes, total_poligonos, total_lineas, total_geometrias, metricas } = analisis;
  const { resumen } = metricas;

  // ✅ CALCULAR longitud total de las líneas
  const longitudTotalKm = (analisis.geometrias || [])
    .filter(g => g.tipo_geometria === 'LINESTRING')
    .reduce((total, linea) => total + (linea.longitud_metros || 0), 0) / 1000;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">📊 {t('analysis.results')}</h3>
        <div className="text-xs text-gray-500">
          Criterios aplicados: {analisis.criterios_aplicados.maquinas_count + 
                                 analisis.criterios_aplicados.operadores_count + 
                                 analisis.criterios_aplicados.faenas_count + 
                                 analisis.criterios_aplicados.predios_count + 
                                 analisis.criterios_aplicados.rodales_count} {t('analysis.filters')}
        </div>
      </div>
      
      {/* ✅ ACTUALIZADA: Grid expandida para incluir líneas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricaCard
          valor={total_reportes}
          label={t('analysis.total_reports')}
          color="blue"
          icon="📋"
        />
        
        <MetricaCard
          valor={total_poligonos || 0}
          label={t('common.area', 'Polígonos')}
          color="green"
          icon="🗺️"
        />
        
        {/* ✅ NUEVO: Tarjeta para líneas */}
        <MetricaCard
          valor={total_lineas || 0}
          label={t('common.description', 'Líneas')}
          color="orange"
          icon="📏"
        />
        
        <MetricaCard
          valor={resumen.total_area_hectareas.toFixed(2)}
          label={t('analysis.total_area')}
          color="purple"
          icon="🏞️"
        />
        
        <MetricaCard
          valor={resumen.total_horas.toFixed(1)}
          label={t('analysis.total_hours')}
          color="blue"
          icon="⏱️"
        />
      </div>

      {/* ✅ Métricas adicionales con longitud */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600">
            {t('common.area')} / {t('analysis.total_reports')}
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {resumen.area_promedio_por_reporte.toFixed(2)} ha
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600">
            {t('common.hours')} / {t('analysis.total_reports')}
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {resumen.horas_promedio_por_reporte.toFixed(1)} h
          </div>
        </div>
        
        {/* ✅ NUEVO: Longitud total */}
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600">
            {t('common.total', 'Longitud total de líneas')}
          </div>
          <div className="text-lg font-semibold text-orange-600">
            {longitudTotalKm.toFixed(2)} km
          </div>
        </div>
        
        {/* ✅ NUEVO: Total geometrías */}
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600">
            {t('common.total')}
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {total_geometrias || (total_poligonos || 0) + (total_lineas || 0)}
          </div>
        </div>
      </div>

      {/* Información de superposiciones si existe */}
      {analisis.superposiciones && analisis.superposiciones.total_superposiciones > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <h4 className="text-sm font-medium text-yellow-800 mb-1">
              ⚠️ {t('common.warning', 'Superposiciones Detectadas')}
            </h4>
            <div className="text-xs text-yellow-700">
              <div>{t('common.total')}: {analisis.superposiciones.total_superposiciones}</div>
              <div>{t('common.area')}: {analisis.superposiciones.area_total_superpuesta_hectareas.toFixed(2)} ha</div>
              <div>%: {analisis.superposiciones.porcentaje_area_superpuesta.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumenMetricas;